import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import usePageSeo from "@/hooks/usePageSeo";

export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  usePageSeo({
    title: "Signing you in — Dividend Portfolio Planner",
    description: "Finalizing your secure sign-in with Google. This page should redirect you to your dashboard in a moment.",
    path: "/auth/callback",
    noindex: true,
  });

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) { nav("/auth", { replace: true }); return; }

    (async () => {
      try {
        const res = await api.post("/auth/session", null, {
          headers: { "X-Session-ID": sessionId },
        });
        setUser(res.data.user);
        // strip the hash and go to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        nav("/dashboard", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Sign-in failed. Please try again.");
      }
    })();
  }, [nav, setUser]);

  if (error) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive grid place-items-center mx-auto">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="serif text-3xl mt-4">Sign-in didn&apos;t complete</h1>
          <p className="text-muted-foreground mt-2 text-sm">{error}</p>
          <Button className="mt-6 rounded-full" onClick={() => nav("/auth", { replace: true })}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] grid place-items-center px-6">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="mt-4 text-muted-foreground text-sm">Finishing sign-in…</p>
      </div>
    </div>
  );
}
